import React, { useState, useRef, useEffect } from 'react';

const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder = 'Search...',
  getOptionLabel = (opt) => opt.label || opt,
  getOptionValue = (opt) => opt.value || opt,
  disabled = false,
  className = '',
  style = {},
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const filtered = options.filter((opt) =>
    getOptionLabel(opt).toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (option) => {
    onChange(getOptionValue(option));
    setOpen(false);
    setSearch('');
    setHighlightedIndex(0);
  };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ') {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        setHighlightedIndex((i) => Math.min(i + 1, filtered.length - 1));
        e.preventDefault();
        break;
      case 'ArrowUp':
        setHighlightedIndex((i) => Math.max(i - 1, 0));
        e.preventDefault();
        break;
      case 'Enter':
        if (filtered[highlightedIndex]) {
          handleSelect(filtered[highlightedIndex]);
        }
        e.preventDefault();
        break;
      case 'Escape':
        setOpen(false);
        setSearch('');
        e.preventDefault();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const selectedOption = options.find((opt) => getOptionValue(opt) === value);
  const selectedLabel = selectedOption ? getOptionLabel(selectedOption) : '';

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', ...style }} className={className}>
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={open ? search : selectedLabel}
        onChange={(e) => {
          setSearch(e.target.value);
          setHighlightedIndex(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        style={{
          width: '100%',
          padding: 'var(--space-2) var(--space-3)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          backgroundColor: 'var(--bg)',
          color: 'var(--text)',
          fontSize: 'var(--font-sm)',
          fontFamily: 'inherit',
        }}
      />

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 'var(--space-2)',
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            zIndex: 1000,
            maxHeight: '200px',
            overflowY: 'auto',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ padding: 'var(--space-3)', color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>
              No options found
            </div>
          ) : (
            filtered.map((option, index) => (
              <div
                key={getOptionValue(option)}
                onClick={() => handleSelect(option)}
                style={{
                  padding: 'var(--space-3)',
                  backgroundColor: index === highlightedIndex ? 'var(--border)' : 'transparent',
                  color: 'var(--text)',
                  fontSize: 'var(--font-sm)',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                }}
              >
                {getOptionLabel(option)}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
